import { Tooltip as TooltipPrimitive } from "@base-ui/react/tooltip";
import { clsx } from "clsx";
import styles from "./Tooltip.module.css";

// Generic in Payload so a handle-driven tooltip infers its trigger's payload type.
function TooltipRoot<Payload>(props: TooltipPrimitive.Root.Props<Payload>) {
	return <TooltipPrimitive.Root {...props} />;
}

// One shared delay for the tooltips inside: the first waits, the neighbours open instantly.
function TooltipProvider(props: TooltipPrimitive.Provider.Props) {
	return <TooltipPrimitive.Provider {...props} />;
}


function TooltipTrigger<Payload>(props: TooltipPrimitive.Trigger.Props<Payload>) {
	return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />;
}

type TooltipPopupProps = TooltipPrimitive.Popup.Props &
	Pick<TooltipPrimitive.Positioner.Props, "side" | "align" | "sideOffset" | "alignOffset"> & {
		portal?: Omit<TooltipPrimitive.Portal.Props, "children">;
		positioner?: Omit<TooltipPrimitive.Positioner.Props, "children">;
	};

// Bundles Portal + Positioner + Popup + Arrow. sideOffset 7 = the legacy .tooltip's 4px arrow box + 3px air.
function TooltipPopup({
	className,
	children,
	side,
	align,
	sideOffset = 7,
	alignOffset,
	portal,
	positioner,
	...props
}: TooltipPopupProps) {
	return (
		<TooltipPrimitive.Portal data-slot="tooltip-portal" {...portal}>
			<TooltipPrimitive.Positioner
				data-slot="tooltip-positioner"
				side={side}
				align={align}
				sideOffset={sideOffset}
				alignOffset={alignOffset}
				{...positioner}
				className={clsx(styles.positioner, positioner?.className)}
			>
				<TooltipPrimitive.Popup data-slot="tooltip-popup" className={clsx(styles.popup, className)} {...props}>
					<TooltipPrimitive.Arrow data-slot="tooltip-arrow" className={styles.arrow} />
					{children}
				</TooltipPrimitive.Popup>
			</TooltipPrimitive.Positioner>
		</TooltipPrimitive.Portal>
	);
}

const Tooltip = {
	Root: TooltipRoot,
	Provider: TooltipProvider,
	Trigger: TooltipTrigger,
	Popup: TooltipPopup,
};

// Lets one tooltip serve many triggers (Base UI handle); Root renders from the active trigger's payload.
const createTooltipHandle = TooltipPrimitive.createHandle;

export { Tooltip, createTooltipHandle };
